import { parseDate } from "helpers/date";
import { parsePrice, centsToDollars } from "helpers/price";
import { Label } from "layout/typography/Label";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLoginWithOrderIdAndDOBMutation } from "store/queries/auth";
import { useGetCardLastDigitsQuery } from "store/queries/stripe";
import { onPublicTokenChange } from "store/slices/auth";
import { IOrder } from "types/Order/Order";
import { IPayment, IPaymentPlan } from "types/Payment";

export function PaymentDetailsSection({
  payments,
  paymentPlan,
  order,
}: {
  payments: IPayment[];
  paymentPlan: IPaymentPlan;
  order?: IOrder;
}) {
  const dispatch = useDispatch();
  const [login] = useLoginWithOrderIdAndDOBMutation();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const hasAttemptedLogin = useRef(false);
  const paidPaymentsSorted = useMemo(() => {
    const paid = payments?.filter((p) => p.status === "Paid") || [];
    return [...paid].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateA - dateB;
    });
  }, [payments]);

  const paymentPlanInfo = useMemo(() => {
    if (!paymentPlan) return null;
    const astivePaymentPlan = paymentPlan;
    const sortedPayments = paidPaymentsSorted;
    const secondPayment = sortedPayments[1];

    return {
      ...astivePaymentPlan,
      secondPayment,
    };
  }, [paymentPlan, paidPaymentsSorted]);

  const showPaymentPlanInfo = useMemo(() => {
    if (!paymentPlan) return false;
    return !!paymentPlan?.installments?.length;
  }, [paymentPlan]);

  const stripeMethodId = useMemo<string | null>(() => {
    if (!showPaymentPlanInfo) {
      return paidPaymentsSorted?.[0]?.stripePaymentMethodId || null;
    }
    return paymentPlanInfo?.stripePaymentMethodId || null;
  }, [showPaymentPlanInfo, paidPaymentsSorted, paymentPlanInfo]);

  const secondCardStripeMethodId = useMemo<string | null>(() => {
    if (!showPaymentPlanInfo) {
      return paidPaymentsSorted?.[1]?.stripePaymentMethodId || null;
    }
    if (paymentPlanInfo?.secondPayment?.splitTotalPaymentId) {
      return paymentPlanInfo?.secondPayment?.stripePaymentMethodId || null;
    }
    return null;
  }, [showPaymentPlanInfo, paidPaymentsSorted, paymentPlanInfo]);

  useEffect(() => {
    if (hasAttemptedLogin.current) return;
    hasAttemptedLogin.current = true;
    const orderId = order?.externalId;
    const dateOfBirth = order?.patient?.dateOfBirth;
    if (!orderId || !dateOfBirth) return;
    login({
      orderId,
      dateOfBirth: new Date(dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token }));
        setIsAuthReady(true);
      })
      .catch(() => {
        setIsAuthReady(false);
      });
  }, [order, login, dispatch]);

  const { data: cardData } = useGetCardLastDigitsQuery(stripeMethodId, {
    skip: !stripeMethodId || !isAuthReady,
  });
  const { data: secondCardData } = useGetCardLastDigitsQuery(
    secondCardStripeMethodId,
    {
      skip: !secondCardStripeMethodId || !isAuthReady,
    }
  );

  const paidPayment: IPayment | undefined = useMemo(() => {
    const firstPaidPayment = payments?.find(
      (aPayment) => aPayment.status === "Paid"
    );

    if (!firstPaidPayment || !firstPaidPayment.splitTotalPaymentId) {
      return firstPaidPayment;
    }

    // If this is a split payment, find all payments with the same splitTotalPaymentId
    const splitPayments = payments?.filter(
      (payment) =>
        payment.splitTotalPaymentId === firstPaidPayment.splitTotalPaymentId &&
        payment.status === "Paid"
    );

    // Calculate total amount across all split payments
    const totalAmountInCents = splitPayments?.reduce(
      (total, payment) => total + (payment.amountInCents || 0),
      0
    );

    // Return the first payment but with the total amount from all split payments
    return {
      ...firstPaidPayment,
      amountInCents: totalAmountInCents,
    };
  }, [payments]);

  const depositDate = useMemo(() => {
    return paidPayment?.stripePayout?.payoutArrivalDate
      ? parseDate(paidPayment.stripePayout.payoutArrivalDate, false, true)
      : "-";
  }, [paidPayment]);
  return (
    <div className="formgrid grid w-100">
      <div className="field flex flex-column col-12 md:col-4 lg:col-3">
        <Label htmlFor="paymentDate">Payment Date</Label>

        <InputText
          disabled
          id="paymentDate"
          aria-labelledby="paymentDate"
          value={
            paidPayment?.createdAt
              ? parseDate(paidPayment.createdAt, false, true)
              : "-"
          }
        />
      </div>

      <div className="field flex flex-column col-12 md:col-4 lg:col-3">
        <Label htmlFor="paymentAmount">Payment Amount</Label>

        <InputText
          disabled
          id="paymentAmount"
          aria-labelledby="paymentAmount"
          value={
            paidPayment?.amountInCents
              ? parsePrice(centsToDollars(paidPayment.amountInCents))
              : "-"
          }
        />
      </div>

      {/* <div className="field flex flex-column col-12 md:col-4">
        <Label htmlFor="depositDate">Deposit Date</Label>

        <InputText
          disabled
          id="depositDate"
          aria-labelledby="depositDate"
          value={depositDate}
        />
      </div> */}

      <div className="field flex flex-column col-12 md:col-4 lg:col-3">
        <Label htmlFor="paymentMethod">Payment Method</Label>

        <InputText
          disabled
          id="paymentMethod"
          value={paidPayment?.type || "-"}
          aria-labelledby="paymentMethod"
        />
      </div>
      {cardData?.data && (
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="cardBrand">Card</Label>
          <InputText
            disabled
            id="cardBrand"
            value={`${cardData.data.brand.toUpperCase()} ending ${
              cardData.data.last4
            }`}
          />
        </div>
      )}
      {secondCardData?.data && (
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="secondCardBrand">Second Card</Label>
          <InputText
            disabled
            id="secondCardBrand"
            value={`${secondCardData.data.brand.toUpperCase()} ending ${
              secondCardData.data.last4
            }`}
          />
        </div>
      )}
    </div>
  );
}
